import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PaymentDetailsView from './PaymentDetailsView';

import useBenefitActivation from '@/presentation/hooks/useActivationBenefitFullCycle';
import { Creators as paymentVouchersActions } from '../../../../../domain/useCases/payments/getPaymentVouchers';
import { Creators as generatePaymentVoucherActions } from '../../../../../domain/useCases/payments/generatePaymentVoucher';
import { Creators as generatePaymentVoucherLateFeeActions } from '../../../../../domain/useCases/payments/generatePaymentVoucherLateFee';
import routes from '@/presentation/components/Routes/routingMap';

type PaymentViewProps = {
  name: string;
  periodDesc: string;
  itemNumber: string;
  amount: number;
  amountWithBenefits: number;
  amountToPay: number;
  status: string;
  hasDiscountProntoPago: boolean;
  paymentType: {
    id: string;
    desc: string;
  };
  dueDate: number;
  recharge: number;
  discountProntoPago: number;
  discountCicloCompleto: number;
  discountBene: number;
  creationDate?: number;
  exonerationDatePSE?: number;
  handlerBack: () => void;
};

const PaymentDetailsController: React.FC = () => {
  const location = useLocation();
  const paymentData = location.state as PaymentViewProps;
  const history = useHistory();
  const [downloadVoucherPDF, setDownloadVoucherPDF] = useState(false);
  const [downloadLateFeePDF, setDownloadLateFeePDF] = useState(false);
  const [showPendingIssuance, setShowPendingIssuance] = useState(false);
  const dispatch = useDispatch();

  const {
    showModal: showModalDeactivateBenefit,
    optionMessage: optionMessageDeactivate,
    isLoadingProgress: isLoadDeactivate,
    toggleBenefit,
    resetModal
  } = useBenefitActivation();

  const {
    data: dataPaymentVouchers,
    isLoading: isLoadingPaymentVoluchers,
    error: errorPaymentVouchers
  } = useSelector((state: any) => state.getPaymentVouchers);
  const { data: dataGenerateVoucher, isLoading: isLoadDownload } = useSelector(
    (state: any) => state.generatePaymentVoucher
  );
  const { data: dataGenerateVoucherLateFee } = useSelector(
    (state: any) => state.generatePaymentVoucherLateFee
  );

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  useEffect(() => {
    if (!paymentData?.itemNumber) {
      history.push('/pagos');
    } else {
      resetModal();
    }
    if (paymentData?.status === 'Pagado') {
      dispatch(
        paymentVouchersActions.getPaymentVouchers(paymentData.itemNumber)
      );
    }
  }, [dispatch, paymentData]);

  const downloadPDF = useCallback(
    (data: { base64PDF: string; fileName: string }, tipo?: string) => {
      if (tipo === 'voucher' && !downloadVoucherPDF) return;
      setShowPendingIssuance(false);
      if (tipo === 'voucher') {
        setDownloadVoucherPDF(false);
      }
      if (tipo === 'lateFee') {
        setDownloadLateFeePDF(false);
      }
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${data.base64PDF}`;
      link.download = data.fileName;
      link.click();
    },
    [downloadVoucherPDF]
  );

  useEffect(() => {
    if (dataGenerateVoucher?.base64PDF && downloadVoucherPDF) {
      downloadPDF(dataGenerateVoucher, 'voucher');
    } else if (
      (!dataGenerateVoucher?.base64PDF || errorPaymentVouchers) &&
      downloadVoucherPDF
    ) {
      setShowPendingIssuance(true);
    }
  }, [dataGenerateVoucher, downloadVoucherPDF]);

  useEffect(() => {
    if (dataGenerateVoucherLateFee?.base64PDF && downloadLateFeePDF) {
      downloadPDF(dataGenerateVoucherLateFee, 'lateFee');
    }
  }, [dataGenerateVoucherLateFee, downloadLateFeePDF]);

  const handleBack = () => history.goBack();

  const handlerRedirect = (route: string) => {
    history.replace(route);
  };

  const handleDonwloadVoucher = () => {
    if (errorPaymentVouchers) {
      setShowPendingIssuance(true);
    }
    if (!dataPaymentVouchers?.length) return;
    setDownloadVoucherPDF(true);
    dispatch(
      generatePaymentVoucherActions.generatePaymentVoucher(
        dataPaymentVouchers[0].companyRut,
        dataPaymentVouchers[0].dteFolio,
        dataPaymentVouchers[0].voucherType,
        dataPaymentVouchers[0].internalSeries,
        dataPaymentVouchers[0].dteDate,
        dataPaymentVouchers[0].amount
      )
    );
    if (dataPaymentVouchers[1]) {
      setDownloadLateFeePDF(true);
      dispatch(
        generatePaymentVoucherLateFeeActions.generatePaymentVoucherLateFee(
          dataPaymentVouchers[1].companyRut,
          dataPaymentVouchers[1].dteFolio,
          dataPaymentVouchers[1].voucherType,
          dataPaymentVouchers[1].internalSeries,
          dataPaymentVouchers[1].dteDate,
          dataPaymentVouchers[1].amount
        )
      );
    }
  };

  const handlerDeactivateDiscount = () => {
    toggleBenefit(false);
  };

  const handleGoPSE = () => {
    history.push({
      pathname: routes.student.procedures.categories.requests.pse.info,
      state: paymentData
    });
  };

  if (!paymentData) {
    history.push('/pagos');
    return null;
  }

  return (
    <PaymentDetailsView
      data={paymentData}
      optionMessage={optionMessageDeactivate}
      isLoadDeactivate={isLoadDeactivate}
      handlerDeactivateDiscount={handlerDeactivateDiscount}
      showModalDeactivateBenefit={showModalDeactivateBenefit}
      handleBack={handleBack}
      handlerRedirect={handlerRedirect}
      handleGoPSE={handleGoPSE}
      handleDonwloadVoucher={handleDonwloadVoucher}
      isLoadingPaymentVoluchers={isLoadingPaymentVoluchers}
      isLoadDownloadVoucher={isLoadDownload}
      showModalPendingIssuance={{ showPendingIssuance, setShowPendingIssuance }}
    />
  );
};

export default PaymentDetailsController;
